import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import Header from "components/Header";
import { routes } from "constants/routes";
import { usePullToRefresh } from "context/PullToRefreshContext";
import useAppStateChange from "hooks/useAppStateChange";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { selectFactoringProfile } from "store/user/selector";
import FRListByStatusDesktop from "../../modules/FRListByStatusDesktop";
import SearchInput from "../../modules/FRListByStatusDesktop/components/SearchInput";
import StatusSummary from "../../Shared/StatusSummary";
import FRTypeFilterDropdown from "../../modules/FRTypeFilterDropdown";
import { useManagePaySummary } from "../../modules/useManagePaySummary";
import { useUserFeature } from "../../../../../hooks/useUserFeature";
import { FR_CONTRACT_TYPE } from "../../../../../constants/haulpay-broker";
import { selectAccount } from "../../../../../store/auth/selector";
import { IOption } from "../../../../../models/Form";

export interface Filters {
  debtor?: string | null;
  status?: string | null;
  orderBy?: string | null;
  loadOrInvoiceOrDebtorOrStatus?: string | undefined | null;
  debtorName?: string | null;
  contractType?: string | null;
}

export default function BrokerManagePay() {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();
  const factoringProfile = useSelector(selectFactoringProfile);
  const { isBroker, isNonFactorRefactorEnabled } = useUserFeature();
  const { userPermission } = useSelector(selectAccount);
  const isTablet = useMediaQuery("(max-width: 980px)");

  const { state, summaryData: rawSummaryData, refetch, handleSearch } = useManagePaySummary();
  useAppStateChange((isActive) => isActive && refetch());
  usePullToRefresh(refetch);

  useEffect(() => {
    if (state?.forceUpdate) {
      navigate(location.pathname, { replace: true });
    }
  }, [state?.forceUpdate, navigate, location.pathname]);

  const summaryData = useMemo(() => {
    const obj = rawSummaryData || {};
    const { all, ...rest } = obj;
    const sortedKeys = Object.keys(rest).sort((a, b) => a.localeCompare(b));

    const sortedObject: any = all ? { all } : {};
    sortedKeys.forEach((key) => {
      if (key !== "hold" || factoringProfile?.enable_hold_review_funding_request_status) {
        sortedObject[key] = rest[key];
      }
    });

    return sortedObject;
  }, [rawSummaryData, factoringProfile?.enable_hold_review_funding_request_status]);

  const [selectedFrTypeOption, setSelectedFrTypeOption] = useState<IOption>();

  const handleFrTypeFilterChange = (selectedFrType: IOption) => {
    setSelectedFrTypeOption(selectedFrType);
    refetch({
      debtor: state?.debtor,
      contractType: selectedFrType.value
    });
  };

  const handleFrTypeFilterReset = () => {
    setSelectedFrTypeOption(undefined);
    refetch({
      debtor: state?.debtor,
      contractType: null
    });
  };

  const frTypeOptions = userPermission
    ? FR_CONTRACT_TYPE.filter(
        (request) => !request.hidden && userPermission.available_contract_types[request.value]
      )
    : [];

  return (
    <MainLayout
      desktopTitle="Manage Pay"
      customHeader={<Header label="Manage Pay" goBackPath={routes.HAULPAY} isHideDesktop />}
      className="gap-5 px-5">
      {!isDesktop || isTablet ? (
        <>
          <SearchInput
            initialValue={""}
            onSearch={handleSearch}
            isMobile={true}
            iconClassName="absolute inset-y-0 my-auto text-placeholder left-4"
          />
          {isBroker && isNonFactorRefactorEnabled && frTypeOptions.length > 0 && (
            <FRTypeFilterDropdown
              style={{ flex: "0" }}
              frTypeOptions={frTypeOptions}
              handleOnChange={handleFrTypeFilterChange}
              handleResetClick={handleFrTypeFilterReset}
            />
          )}
          <StatusSummary
            summaryData={summaryData}
            debtor={state?.debtor}
            debtorName={state?.debtorName}
            frTypeOption={selectedFrTypeOption}
          />
        </>
      ) : (
        <FRListByStatusDesktop
          summaryData={summaryData}
          debtor={state?.debtor}
          refetchSummaryData={refetch}
          debtorName={state?.debtorName}
        />
      )}
    </MainLayout>
  );
}
